import { Modal, Select } from 'antd';
import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { API_URL, TOKEN } from '../../../variables/constants';

const AddFractionnement = ({ show, handleClose, editMode, selectedFractionnement, onSaved }) => {
  // State variables for form fields
  const [nom, setNom] = useState('');
  const [nombre, setNombre] = useState('');
  const [libelleFractionnement, setLibelleFractionnement] = useState('');
  const [description, setDescription] = useState('');
  const [moisMin, setMoisMin] = useState('');
  const [moisMax, setMoisMax] = useState('');
  const [categorie, setCategorie] = useState('');
  const [selectedGaranties, setSelectedGaranties] = useState([]); // Array of STRING codeIds for Ant Select
  const [loading, setLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]); // All categories
  const [garantiesList, setGarantiesList] = useState([]); // All guarantees
  const [catGaranties, setCatGaranties] = useState([]); // Guarantees filtered by selected category (for Select options)
  const [periode, setPeriode] = useState("MOIS");

  // --- Data Fetching ---
  // Fetch categories and all guarantees when the component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [categoriesRes, garantiesRes] = await Promise.all([
          axios.get(`${API_URL}/api/categorie`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
          axios.get(`${API_URL}/api/garantie`, { headers: { Authorization: `Bearer ${TOKEN}` } }),
        ]);
        setCategoriesList(categoriesRes.data || []);
        setGarantiesList(garantiesRes.data || []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
        toast.error("Erreur lors du chargement des données initiales");
      }
    };
    fetchInitialData();
  }, []); // Empty dependency array means this runs once on mount

  // --- Form Reset Logic ---
  // Use useCallback to memoize the reset function
  const resetForm = useCallback(() => {
    setNom('');
    setNombre('');
    setLibelleFractionnement('');
    setDescription('');
    setMoisMin('');
    setMoisMax('');
    setCategorie('');
    setSelectedGaranties([]); // Crucial to reset selected guarantees
    setCatGaranties([]); // And available guarantees
    setPeriode("MOIS");
  }, []);

  // --- Initialization for Edit Mode / Reset for Add Mode ---
  useEffect(() => {
    if (editMode && selectedFractionnement) {
      // Only proceed if both categoriesList and garantiesList are loaded
      if (categoriesList.length > 0 && garantiesList.length > 0) {
        const {
          nom,
          nombre,
          libelleFractionnement,
          description,
          moisMin,
          moisMax,
          categorie: selectedCatObject, // Renamed to avoid conflict with the state variable
          garanties,
          periode
        } = selectedFractionnement;

        // Set basic fields
        setNom(nom || '');
        setNombre(nombre || '');
        setLibelleFractionnement(libelleFractionnement || '');
        setDescription(description || '');
        setMoisMin(moisMin || '');
        setMoisMax(moisMax || '');
        setPeriode(periode || "MOIS");

        // Set category and filter available guarantees based on it
        const codeCat = selectedCatObject?.codeCategorie;
        setCategorie(codeCat || '');

        // Filter `garantiesList` to get `catGaranties` based on the selected category
        if (codeCat) {
          const filtered = garantiesList.filter(g => g.categorie?.codeCategorie === codeCat);
          setCatGaranties(filtered);
        } else {
          setCatGaranties([]);
        }

        // Set the selected guarantees for the Ant Design Select
        // It must be an array of STRINGS matching the `value` in `Select` options
        if (garanties && Array.isArray(garanties)) {
          const guaranteeCodeIds = garanties.map(gar => String(gar.codeId)).filter(Boolean);
          setSelectedGaranties(guaranteeCodeIds);
        } else {
          setSelectedGaranties([]);
        }
      }
    } else {
      // If not in edit mode (i.e., add mode), or if `show` changes, reset the form
      resetForm();
    }
  }, [editMode, selectedFractionnement, categoriesList, garantiesList, resetForm, show]); // Added `show` to dependencies

  // --- Dynamic Filtering of Guarantees ---
  // This useEffect ensures `catGaranties` is updated whenever `categorie` or `garantiesList` changes
  useEffect(() => {
    if (categorie && garantiesList.length > 0) {
      const filtered = garantiesList.filter(
        (gl) => gl.categorie?.codeCategorie === categorie
      );
      setCatGaranties(filtered);
    } else if (!categorie) {
      setCatGaranties([]); // Clear if no category selected
    }
  }, [categorie, garantiesList]);


  // --- Event Handlers ---
  const handleGarantiesChange = (value) => {
    // `value` from Ant Design Select is already an array of strings (the codeIds)
    setSelectedGaranties(value);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const selectedCategoryObject = categoriesList.find((c) => c.codeCategorie === categorie);

    // Prepare data for API call
    const fractionnementData = {
      nom,
      nombre: parseInt(nombre),
      libelleFractionnement,
      description,
      periode,
      moisMin: parseInt(moisMin),
      moisMax: parseInt(moisMax),
      categorie: selectedCategoryObject,
      // Map back the string IDs from `selectedGaranties` to full guarantee objects for the API
      garanties: selectedGaranties
        .map((gCodeId) => garantiesList.find((gar) => String(gar.codeId) === gCodeId))
        .filter(Boolean), // Filter out any undefined guarantees if not found
    };

    try {
      if (editMode) {
        // For update, include the existing ID from selectedFractionnement
        const updatedFractionnement = {
          ...selectedFractionnement,
          ...fractionnementData,
        };

        await axios.put(`${API_URL}/api/fractionnement`, updatedFractionnement, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        toast.success("Fractionnement modifié avec succès");
      } else {
        await axios.post(`${API_URL}/api/fractionnement`, fractionnementData, {
          headers: {
            Authorization: `Bearer ${TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        toast.success("Fractionnement ajouté avec succès");
      }

      onSaved(); // Notify parent component to refresh data and close modal
    } catch (err) {
      console.error("Erreur lors de l'opération :", err.response?.data || err.message);
      toast.error("Échec de l'opération");
    } finally {
      setLoading(false);
    }
  };

  // --- Render JSX ---
  return (
    <Modal open={show} onCancel={handleClose} footer={null} centered>
      <div className="text-center mb-6">
        <h4 className="address-title mb-2">
          {editMode ? "Modifier le fractionnement" : "Ajouter un fractionnement"}
        </h4>
        <p className="address-subtitle">
          {editMode ? "Modifier les informations du fractionnement" : "Ajouter un nouveau fractionnement"}
        </p>
      </div>
      <form onSubmit={onSubmit}>
        {/* Libellé Fractionnement */}
        <div className="form-group mb-3">
          <label htmlFor="libelleFractionnement" className="form-label">Nom</label>
          <input
            type="text"
            id="libelleFractionnement"
            className="form-control"
            placeholder="Libellé de fractionnement"
            value={libelleFractionnement}
            onChange={(e) => setLibelleFractionnement(e.target.value)}
            required
          />
        </div>

        {/* Catégorie Select */}
        <div className="form-group mb-3">
          <label htmlFor="categorie" className="form-label">Catégorie</label>
          <select
            id="categorie"
            className="form-select"
            value={categorie}
            onChange={(e) => {
              const selectedCodeCat = e.target.value;
              setCategorie(selectedCodeCat);
              setSelectedGaranties([]); // Clear selected guarantees if category changes
            }}
            required
          >
            <option value="">Sélectionner une catégorie</option>
            {categoriesList.map((cat) => (
              <option key={cat.categorieId} value={cat.codeCategorie}>
                {cat.nom}
              </option>
            ))}
          </select>
        </div>

        {/* Garanties Multi-Select */}
        <div className="form-group mb-3">
          <label htmlFor="garanties" className="form-label">Garanties</label>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Sélectionner les garanties"
            value={selectedGaranties} // This *must* be an array of STRING codeIds
            onChange={handleGarantiesChange}
            options={catGaranties.map((garantie) => ({
              label: garantie.nom,
              value: String(garantie.codeId), // Ensure the value is a string, robustly
            }))}
            disabled={!categorie} // Disable if no category is selected
            showSearch // Allows searching through options
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Nombre */}
        <div className="form-group mb-3">
          <label htmlFor="nombre" className="form-label">Nombre</label>
          <input
            type="number"
            id="nombre"
            className="form-control"
            placeholder="Nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            required
          />
        </div>

        {/* Période Select */}
        <div className="form-group mb-3">
          <label htmlFor="periode" className="form-label">Période</label>
          <select
            id="periode"
            className="form-select"
            value={periode}
            onChange={(e) => setPeriode(e.target.value)}
          >
            <option value="MOIS">MOIS</option>
            <option value="JOUR">JOUR</option>
            <option value="AN">AN</option>
          </select>
        </div>

        {/* Mois Minimum */}
        <div className="form-group mb-3">
          <label htmlFor="moisMin" className="form-label">Période Minimum ({periode})</label>
          <input
            type="number"
            id="moisMin"
            className="form-control"
            placeholder={`Minimum de ${periode.toLowerCase()}`}
            value={moisMin}
            onChange={(e) => setMoisMin(e.target.value)}
            required
          />
        </div>

        {/* Mois Maximum */}
        <div className="form-group mb-3">
          <label htmlFor="moisMax" className="form-label">Période Maximum ({periode})</label>
          <input
            type="number"
            id="moisMax"
            className="form-control"
            placeholder={`Maximum de ${periode.toLowerCase()}`}
            value={moisMax}
            onChange={(e) => setMoisMax(e.target.value)}
            required
          />
        </div>

        {/* Description */}
        <div className="form-group mb-4">
          <label htmlFor="description" className="form-label">Description</label>
          <textarea
            id="description"
            className="form-control"
            placeholder="Description du fractionnement"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>

        {/* Action Buttons */}
        <div className="text-center mt-4">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Enregistrement..." : "Enregistrer"}
          </button>
          <button type="button" className="btn btn-secondary ms-2" onClick={handleClose}>
            Annuler
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddFractionnement;